<template>
  <div class="data-table">
    <!-- 상단 툴바 (검색 + 좌/우 액션·엑셀다운로드 등) -->
    <div v-if="searchable || $slots['toolbar-left'] || $slots['toolbar-right']" class="dt-toolbar">
      <div class="dt-toolbar-left">
        <div v-if="searchable" class="dt-search">
          <input
            v-model="searchInput"
            :placeholder="searchPlaceholder"
            class="dt-search-input"
            @keyup.enter="emitSearch"
          />
          <button type="button" class="dt-search-btn" @click="emitSearch">검색</button>
          <button v-if="searchInput" type="button" class="dt-search-clear" @click="clearSearch">✕</button>
        </div>
        <slot name="toolbar-left" />
      </div>
      <div class="dt-toolbar-right"><slot name="toolbar-right" /></div>
    </div>

    <!-- 스크롤 래퍼 (auto/고정 높이 시 본문만 세로 스크롤 + 헤더 고정) -->
    <div ref="scrollRef" class="dt-scroll" :style="scrollStyle">
      <table class="dt-table">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              :style="thStyle(col)"
              :class="`align-${col.align || 'left'}`"
            >
              <!-- header-{key} 슬롯으로 완전 커스텀 가능. 기본은 라벨+정렬+필터 슬롯 -->
              <slot
                :name="`header-${col.key}`"
                :col="col"
                :sort="() => onSort(col.key)"
                :indicator="sortIndicator(col.key)"
              >
                <div class="dt-th">
                  <span
                    class="dt-th-label"
                    :class="{ sortable: col.sortable }"
                    @click="col.sortable ? onSort(col.key) : null"
                  >
                    {{ col.label }}
                    <span v-if="col.sortable" class="dt-sort">{{ sortIndicator(col.key) }}</span>
                  </span>
                  <!-- 컬럼별 필터 UI (select/input 등) — 클릭이 정렬로 안 번지게 stop -->
                  <span v-if="$slots[`filter-${col.key}`]" class="dt-filter" @click.stop>
                    <slot :name="`filter-${col.key}`" :col="col" />
                  </span>
                </div>
              </slot>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="columns.length" class="dt-empty">불러오는 중…</td>
          </tr>
          <tr v-else-if="!rows.length">
            <td :colspan="columns.length" class="dt-empty">데이터가 없습니다.</td>
          </tr>
          <tr
            v-for="(row, index) in rows"
            v-else
            :key="rowKeyOf(row, index)"
            :class="rowClass ? rowClass(row) : null"
          >
            <td
              v-for="col in columns"
              :key="col.key"
              :style="tdStyle(col)"
              :class="`align-${col.align || 'left'}`"
            >
              <slot :name="`cell-${col.key}`" :row="row" :value="row[col.key]" :index="rowIndex(index)">
                {{ row[col.key] }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 페이징 -->
    <div v-if="paged" class="dt-pager">
      <div class="dt-pager-info">
        총 {{ totalElements }}건 · {{ totalPages || 1 }}페이지 중 {{ page + 1 }}
      </div>
      <div class="dt-pager-controls">
        <button class="dt-page-btn" :disabled="page <= 0" @click="goTo(page - 1)">‹</button>
        <button
          v-for="p in pageWindow"
          :key="p"
          class="dt-page-btn"
          :class="{ active: p === page }"
          @click="goTo(p)"
        >{{ p + 1 }}</button>
        <button class="dt-page-btn" :disabled="page >= totalPages - 1" @click="goTo(page + 1)">›</button>
      </div>
      <div class="dt-pager-size">
        <select :value="size" @change="onSizeChange($event.target.value)">
          <option v-for="opt in pageSizeOptions" :key="opt" :value="opt">{{ opt }}개씩</option>
        </select>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';

const props = defineProps({
  // [{ key, label, minWidth?, width?, sortable?, align? }]
  columns: { type: Array, required: true },
  rows: { type: Array, default: () => [] },
  // 행 :key — 필드명(string) 또는 (row)=>key 함수. 없으면 index
  rowKey: { type: [String, Function], default: null },
  // 행 class 계산 함수 (row)=>object|string
  rowClass: { type: Function, default: null },
  loading: { type: Boolean, default: false },

  // 페이징 (paged=false 면 전체 렌더, 컨트롤 숨김)
  paged: { type: Boolean, default: false },
  page: { type: Number, default: 0 }, // 0-based
  size: { type: Number, default: 20 },
  totalElements: { type: Number, default: 0 },
  totalPages: { type: Number, default: 0 },
  pageSizeOptions: { type: Array, default: () => [20, 50, 100] },

  // 정렬 (서버 정렬 — 부모가 상태 보유)
  sortKey: { type: String, default: '' },
  sortDirection: { type: String, default: 'ASC' }, // 'ASC' | 'DESC'

  // 검색 (서버 검색 — 부모가 keyword 보유, @search 로 수신)
  searchable: { type: Boolean, default: false },
  searchPlaceholder: { type: String, default: '검색어 입력' },

  // 본문 세로 스크롤 영역 높이.
  // 'auto'(기본) = 표의 실제 화면 위치를 측정해 뷰포트에 맞춤(레이아웃 chrome 자동 대응, 2중 스크롤 방지).
  // 'none' = 제한 없음(페이지 스크롤). 'calc(...)'/'60vh' 등 고정값도 가능.
  maxHeight: { type: String, default: 'auto' },
});

const emit = defineEmits(['update:page', 'update:size', 'sort', 'search']);

const searchInput = ref('');
const emitSearch = () => emit('search', searchInput.value.trim());
const clearSearch = () => {
  searchInput.value = '';
  emit('search', '');
};

// auto 가 아닌 고정값일 때만 inline style 로 적용. auto/none 은 JS(applyAutoHeight)가 처리.
const scrollStyle = computed(() =>
  props.maxHeight && props.maxHeight !== 'none' && props.maxHeight !== 'auto'
    ? { maxHeight: props.maxHeight, overflowY: 'auto' }
    : {}
);

// 동적 높이 — chrome 추측 없이 "실제 페이지 오버플로우"만큼만 스크롤 영역을 줄여 페이지가 딱 맞게.
// 1) 제한 해제 → 자연 높이로 둠  2) 페이지가 넘치면 그 초과분만큼 스크롤 영역 max-height 축소.
const scrollRef = ref(null);
const SAFETY = 8; // 서브픽셀/경계 여유
const applyAutoHeight = () => {
  if (props.maxHeight !== 'auto') return;
  const el = scrollRef.value;
  if (!el) return;
  el.style.overflowY = 'auto';
  el.style.maxHeight = 'none'; // 자연 높이 (reflow 유도)
  const overflow = document.documentElement.scrollHeight - window.innerHeight;
  if (overflow > 0) {
    const target = Math.max(200, el.clientHeight - overflow - SAFETY);
    el.style.maxHeight = `${target}px`;
  }
  // overflow <= 0 이면 페이지가 안 넘치므로 제한 불필요(none 유지) → 페이지 스크롤 없음
};

const thStyle = (col) => ({
  minWidth: col.minWidth || '80px',
  width: col.width || 'auto',
});
const tdStyle = (col) => ({ minWidth: col.minWidth || '80px' });

onMounted(() => {
  nextTick(applyAutoHeight);
  window.addEventListener('resize', applyAutoHeight);
});
onBeforeUnmount(() => window.removeEventListener('resize', applyAutoHeight));
// 행 수/로딩 변화로 위쪽 레이아웃이 흔들릴 수 있어 재측정
watch(() => [props.rows.length, props.loading], () => nextTick(applyAutoHeight));

const rowKeyOf = (row, index) => {
  if (typeof props.rowKey === 'function') return props.rowKey(row);
  if (typeof props.rowKey === 'string') return row[props.rowKey];
  return index;
};
// paged 면 전역 순번(1부터)을 슬롯에 넘겨주기 위한 베이스
const rowIndex = (index) => (props.paged ? props.page * props.size + index : index);

const onSort = (key) => emit('sort', key);
const sortIndicator = (key) => {
  if (props.sortKey !== key) return '↕';
  return props.sortDirection === 'ASC' ? '▲' : '▼';
};

const goTo = (p) => {
  if (p < 0 || (props.totalPages && p > props.totalPages - 1)) return;
  emit('update:page', p);
};
const onSizeChange = (val) => {
  // 크기 변경 → 부모가 page 0 으로 리셋 + 재조회 (이중 emit 방지)
  emit('update:size', Number(val));
};

// 현재 페이지 주변 최대 7개 윈도우
const pageWindow = computed(() => {
  const total = props.totalPages || 1;
  const cur = props.page;
  const span = 7;
  let start = Math.max(0, cur - Math.floor(span / 2));
  let end = Math.min(total - 1, start + span - 1);
  start = Math.max(0, Math.min(start, end - span + 1));
  const arr = [];
  for (let i = start; i <= end; i++) arr.push(i);
  return arr;
});
</script>

<style scoped>
/* 상단 툴바 */
.dt-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.dt-toolbar-left,
.dt-toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.dt-search {
  display: flex;
  align-items: center;
  gap: 6px;
}
.dt-search-input {
  height: 34px;
  width: 240px;
  max-width: 50vw;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
}
.dt-search-btn {
  height: 34px;
  padding: 0 14px;
  border: 1px solid #2563eb;
  background: #2563eb;
  color: #fff;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}
.dt-search-clear {
  height: 34px;
  width: 34px;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 4px;
  color: #6b7280;
  cursor: pointer;
}

.dt-scroll {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
}
/* 가로·세로 스크롤바를 통통하게 — 얇은 오버레이 스크롤바가 잡기 어렵다는 피드백(#178).
   전역 reset.css 는 height:3px/width:8px + 투명한 흰 thumb 라 특히 잡기 어려움 → 여기서 덮어씀.
   주의: 표준 scrollbar-width 를 지정하면 모던 Chrome(121+)이 아래 ::-webkit-scrollbar 를
   무시하므로 일부러 지정하지 않는다(webkit 계열에서 항상 보이는 두꺼운 스크롤바 유지). */
.dt-scroll::-webkit-scrollbar {
  width: 14px; /* 세로 스크롤바 */
  height: 14px; /* 가로 스크롤바 */
}
.dt-scroll::-webkit-scrollbar-track {
  background: #eef0f3;
  border-radius: 6px;
}
.dt-scroll::-webkit-scrollbar-thumb {
  background: #b8bcc4;
  border-radius: 8px;
  border: 3px solid #eef0f3; /* 트랙과 여백 → 손잡이가 도톰해 보이게 */
}
.dt-scroll::-webkit-scrollbar-thumb:hover {
  background: #9aa0a9;
}
.dt-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto; /* 내용맞춤 폭 */
  font-size: 13px;
  white-space: nowrap; /* 셀이 줄바꿈으로 찌부되지 않게 — 넘치면 가로 스크롤 */
}
.dt-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f7f8fa;
  color: #374151;
  font-weight: 600;
  text-align: left;
  padding: 12px 14px;
  border-bottom: 1px solid #e5e7eb;
}
.dt-th {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}
.dt-th-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.dt-th-label.sortable {
  cursor: pointer;
  user-select: none;
}
.dt-sort {
  font-size: 10px;
  color: #9ca3af;
}
.dt-filter {
  font-weight: 400;
}
/* 검색 input(34px)과 높이·스타일 일관 — 글로벌 select 높이 override */
.dt-filter :deep(select),
.dt-filter :deep(input) {
  height: 34px;
  min-width: 120px;
  max-width: 180px;
  padding: 0 10px;
  font-size: 13px;
  line-height: 34px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
}
.dt-table tbody td {
  padding: 12px 14px;
  border-bottom: 1px solid #f0f1f3;
  color: #1f2937;
}
.dt-table tbody tr:hover {
  background: #fafbfc;
}
.align-center { text-align: center; }
.align-right { text-align: right; }
.dt-empty {
  text-align: center;
  color: #9ca3af;
  padding: 40px 0;
}

/* 페이저 */
.dt-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  flex-wrap: wrap;
}
.dt-pager-info {
  font-size: 13px;
  color: #6b7280;
}
.dt-pager-controls {
  display: flex;
  gap: 4px;
}
.dt-page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
}
.dt-page-btn:hover:not(:disabled) { background: #f3f4f6; }
.dt-page-btn.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
  font-weight: 600;
}
.dt-page-btn:disabled { opacity: 0.4; cursor: default; }
.dt-pager-size select {
  height: 32px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 13px;
}
</style>
